import { FC, ReactNode, SetStateAction, useState } from "react";
import { Link } from "react-router-dom";
import ReactTooltip from "react-tooltip";

import { LocationDescriptor } from "history";
import useAppSelector from "hooks/useAppSelector";
import { useDeleteRefundOSW } from "modules/oneSpaWorld/hooks/useDeleteRefundOSW";
import {
  updateBookings,
  updateMusementBookings,
  updateMxpBookings,
  updateOpenRefunds,
  updateOswBookings,
} from "actions/cartActions";
import CenteredImageContainer from "components/CenteredImageContainer";
import { getResourceImageUrl } from "utils/imageUtils";
import SvgIcon from "components/atoms/SvgIcon";
import Modal from "components/modals/Modal/Modal";
import TextInputWithLabel from "components/TextInputWithLabel";
import ModalFooter from "components/modals/ModalFooter";
import Price from "components/Price/Price";
import Loader from "components/atoms/Loader";
import { useDeleteAgencyRefundOSW } from "modules/oneSpaWorld/hooks/useDeleteAgencyRefundOSW";

type Props = {
  name: string;
  image: string | null;
  imageCategory?: any;
  linkTo: string | LocationDescriptor;
  tooltipText: string;
  treatmentId: number;
  transactionId: string;
  appointmentId: number;
  bookedByAgency: boolean;
  price: number;
  time: string;
  children: ReactNode;
};
const CartElementOswTreatmentBooked: FC<Props> = ({
  name,
  image,
  imageCategory,
  linkTo,
  tooltipText,
  treatmentId,
  transactionId,
  appointmentId,
  bookedByAgency,
  price,
  time,
  children,
}) => {
  const [showRefundModal, setShowRefundModal] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [refundReference, setRefundReference] = useState("");
  const { deleteRefundOSW, loading: deleteRefundOSWIsLoading } =
    useDeleteRefundOSW();
  const { deleteAgencyRefundOSW, deleteAgencyRefundOSWIsLoading } =
    useDeleteAgencyRefundOSW();
  const agencySessionType = useAppSelector(
    (state) => state?.agency?.sessionType
  );
  const isLoggedAsAgency = agencySessionType === "SingleSignOn";

  const processRefund = async () => {
    if (bookedByAgency && isLoggedAsAgency) {
      await deleteAgencyRefundOSW({ transactionId, appointmentId });
    } else {
      await deleteRefundOSW({ transactionId, appointmentId });
    }

    setShowConfirmationModal(false);
    setRefundReference("");

    await updateBookings();
    await updateOswBookings();
    await updateMusementBookings();
    await updateMxpBookings();
    await updateOpenRefunds();
  };

  const handleRefundClick = () => {
    setShowRefundModal(false);
    setShowConfirmationModal(true);
  };

  const isButtonDisabled = () => {
    // agency needs to provide refund reference
    if (isLoggedAsAgency) {
      return (
        !refundReference || !bookedByAgency || deleteAgencyRefundOSWIsLoading
      );
    } else {
      return bookedByAgency || deleteRefundOSWIsLoading;
    }
  };

  if (deleteRefundOSWIsLoading || deleteAgencyRefundOSWIsLoading) {
    return <Loader />;
  }

  return (
    <div>
      <div className="flex flex-wrap bg-blue-lightest shadow sm:p-4 my-4">
        {/* image */}
        <div className="w-full sm:w-1/2 md:w-2/5 xl:w-1/3">
          <CenteredImageContainer
            alt={name + " cover"}
            src={getResourceImageUrl(image, {
              width: 400,
              category: imageCategory,
            })}
          />
        </div>

        {/* rest of card */}
        <div className="sm:flex sm:flex-col px-4 py-2 sm:p-0 w-full sm:w-1/2 md:w-3/5 xl:w-2/3 sm:pl-4 mt-2 sm:mt-0 relative">
          {/* Cancel the booking - show refund modal */}
          <span
            className="text-blue cursor-pointer absolute top-0 right-0 mr-4 mt-2 sm:m-0"
            onClick={() => {
              setShowRefundModal(true);
            }}
          >
            <span className="hidden md:inline">Cancel</span>{" "}
            <SvgIcon name="x" className="w-3 h-3 inline ml-1" />
          </span>

          {/* title link */}
          <div className="mr-6 md:mr-16">
            <Link
              className="text-blue underline text-2xl uppercase sm:pr-3 md:pr-0"
              to={linkTo}
            >
              {name}
            </Link>
          </div>

          {/* details */}
          <div className="sm:flex-grow">
            <div className="my-2">{children}</div>
          </div>
          {/* cost info */}
          <div className="text-right">
            Already purchased -
            <span
              className="cursor-pointer ml-2 text-blue border-dotted border-b border-blue"
              data-tip={tooltipText}
              data-for={treatmentId}
              data-effect="solid"
              data-place="bottom"
              data-multiline={true}
            >
              See cost
            </span>
          </div>

          <ReactTooltip id={treatmentId.toString()} />
        </div>
      </div>

      {/* Refund modal */}
      <Modal
        show={showRefundModal}
        onClose={() => {
          setShowRefundModal(false);
        }}
        title="Cancel booking"
      >
        <div className="flex flex-grow flex-col">
          <div className="flex-grow bg-white py-4 px-2 sm:px-4">
            <div>
              <div className="text-lg font-bold">
                Issue refund for the following booking
              </div>
              <div className="bg-blue-lightest p-4 mt-4">
                <div className="text-xl">{name}</div>
                <div className="mt-1">
                  Spa treatment booking for one person at {time}
                </div>
              </div>
              <div className="mt-2">
                <Price preText="Refund value" price={price.toString()} />
              </div>
              {isLoggedAsAgency && (
                <div className="my-4">
                  <TextInputWithLabel
                    label="Refund Reference"
                    type="text"
                    value={refundReference}
                    _onChange={(value: SetStateAction<string>) =>
                      setRefundReference(value)
                    }
                    placeholder=""
                    errorMessage=""
                  />
                </div>
              )}
            </div>

            {((bookedByAgency && !isLoggedAsAgency) ||
              (!bookedByAgency && isLoggedAsAgency)) && (
              <div className="text-lg font-bold py-4">
                Bookings can only be refunded via the same channel they were
                booked
              </div>
            )}
          </div>
          <ModalFooter
            onCancel={() => {
              setShowRefundModal(false);
            }}
            actionDisabled={isButtonDisabled()}
            actionButtonText={
              deleteRefundOSWIsLoading || deleteAgencyRefundOSWIsLoading
                ? "Please wait..."
                : "Refund booking"
            }
            onActionClick={handleRefundClick}
          />
        </div>
      </Modal>

      {/* Confirmation modal */}
      <Modal
        show={showConfirmationModal}
        onClose={() => {
          setShowConfirmationModal(false);
        }}
        title="Confirmation"
      >
        <div className="flex flex-grow flex-col">
          <div className="flex-grow bg-white py-4 px-2 sm:px-4">
            <div className="text-xl font-bold">
              Are you sure you want to cancel this booking?
            </div>
          </div>

          <ModalFooter
            cancelLabel="No"
            onCancel={() => {
              setShowConfirmationModal(false);
            }}
            actionDisabled={
              deleteRefundOSWIsLoading || deleteAgencyRefundOSWIsLoading
            }
            actionButtonText={
              deleteRefundOSWIsLoading || deleteAgencyRefundOSWIsLoading
                ? "Please wait..."
                : "Yes - cancel"
            }
            onActionClick={processRefund}
          />
        </div>
      </Modal>
    </div>
  );
};

export default CartElementOswTreatmentBooked;
